import {
  SandpackProvider,
  SandpackLayout,
  SandpackCodeEditor,
  SandpackPreview,
  SandpackFileExplorer,
  SandpackConsole,
  useSandpack,
} from "@codesandbox/sandpack-react";
import { useImperativeHandle, forwardRef, useEffect, useRef } from "react";

// Match our app theme so Sandpack panels feel native.
const theme = {
  colors: {
    surface1: "#05060a",
    surface2: "#0a0c14",
    surface3: "#10131f",
    clickable: "rgba(255,255,255,0.55)",
    base: "rgba(255,255,255,0.92)",
    disabled: "rgba(255,255,255,0.3)",
    hover: "#ffffff",
    accent: "#3a5bff",
    error: "#ff6b6b",
    errorSurface: "rgba(255,107,107,0.1)",
  },
  syntax: {
    plain: "#e2e8f0",
    comment: { color: "#64748b", fontStyle: "italic" },
    keyword: "#a78bfa",
    tag: "#ff85bd",
    punctuation: "#94a3b8",
    definition: "#5d7bff",
    property: "#5d7bff",
    static: "#ff4ca0",
    string: "#9be0a8",
  },
  font: {
    body: "Inter, ui-sans-serif, system-ui, sans-serif",
    mono: "JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, monospace",
    size: "13px",
    lineHeight: "1.55",
  },
};

const MOBILE_PREVIEW_WIDTH = 390;
const MOBILE_PREVIEW_HEIGHT = 844;

// Sandpack sets inline iframe height from bundler "resize" events — override for phone frame.
function MobilePreviewHeightLock({ containerRef, height = MOBILE_PREVIEW_HEIGHT }) {
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const apply = () => {
      const px = `${height}px`;
      root.querySelectorAll(
        ".sp-stack, .sp-preview, .sp-preview-container, iframe[title='Sandpack Preview']"
      ).forEach((el) => {
        el.style.setProperty("height", px, "important");
        el.style.setProperty("min-height", px, "important");
        el.style.setProperty("max-height", "none", "important");
        el.style.setProperty("flex", "none", "important");
      });
    };

    apply();
    const observer = new MutationObserver(apply);
    observer.observe(root, { attributes: true, childList: true, subtree: true });
    const timer = window.setInterval(apply, 800);
    return () => {
      observer.disconnect();
      window.clearInterval(timer);
    };
  }, [containerRef, height]);

  return null;
}

// Bridge that lets the parent page push AI-generated code into Sandpack files.
function SandpackBridge({ bridgeRef, primaryFile }) {
  const { sandpack } = useSandpack();
  useImperativeHandle(
    bridgeRef,
    () => ({
      writeFile(path, code) {
        sandpack.updateFile(path, code, true);
        sandpack.runSandpack();
      },
      writePrimary(code) {
        sandpack.updateFile(primaryFile, code, true);
        sandpack.runSandpack();
      },
      getFile(path) {
        return sandpack.files[path]?.code ?? "";
      },
      getPrimary() {
        return sandpack.files[primaryFile]?.code ?? "";
      },
      getAllFiles() {
        return Object.fromEntries(
          Object.entries(sandpack.files).map(([path, file]) => [
            path,
            file?.code ?? "",
          ])
        );
      },
      listFiles() {
        return Object.keys(sandpack.files);
      },
    }),
    [sandpack, primaryFile]
  );
  return null;
}

const ProjectSandpack = forwardRef(function ProjectSandpack(
  {
    template = "react",
    files,
    customSetup,
    primaryFile = "/App.js",
    showExplorer = false,
    bridgeRef,
    viewMode = "split", // "code" | "split" | "preview"
    showConsole = false,
    deviceFrame = false, // "mobile" for React Native phone dimensions
  },
  _outerRef
) {
  const mobileDeviceRef = useRef(null);
  const showCode = viewMode !== "preview";
  const showPreview = viewMode !== "code";
  const mobilePreview = deviceFrame === "mobile";

  const codeFlex = viewMode === "code" ? 1 : 0.8;
  const previewFlex = viewMode === "preview" ? 1 : 1.4;

  const providerProps = {
    ...(template ? { template } : {}),
    files,
    customSetup,
    theme,
    style: { height: "100%" },
    options: {
      recompileMode: "immediate",
      recompileDelay: 300,
      autoReload: true,
      activeFile: primaryFile,
      editorHeight: "100%",
      editorWidthPercentage: mobilePreview ? 55 : 42,
    },
  };

  if (mobilePreview) {
    return (
      <SandpackProvider {...providerProps}>
        <SandpackBridge bridgeRef={bridgeRef} primaryFile={primaryFile} />
        <div className="sandpack-mobile-layout">
          {showExplorer && showCode && (
            <SandpackFileExplorer style={{ height: "100%", minWidth: 180 }} />
          )}
          {showCode && (
            <SandpackCodeEditor
              showLineNumbers
              showInlineErrors
              wrapContent
              style={{ height: "100%", flex: 1, minWidth: 0 }}
            />
          )}
          {showPreview && (
            <div
              className="sandpack-preview-column-mobile"
              style={{
                width: showCode ? MOBILE_PREVIEW_WIDTH + 56 : "100%",
                flexShrink: 0,
              }}
            >
              <div className="sandpack-mobile-pane">
                <div
                  ref={mobileDeviceRef}
                  className="sandpack-mobile-device"
                  data-testid="mobile-preview-frame"
                >
                  <SandpackPreview
                    showOpenInCodeSandbox={false}
                    showRefreshButton
                  />
                  <MobilePreviewHeightLock containerRef={mobileDeviceRef} />
                </div>
              </div>
              {showConsole && (
                <SandpackConsole
                  style={{
                    height: 120,
                    borderTop: "1px solid rgba(255,255,255,0.06)",
                    flexShrink: 0,
                  }}
                  resetOnPreviewRestart
                />
              )}
            </div>
          )}
        </div>
      </SandpackProvider>
    );
  }

  return (
    <SandpackProvider {...providerProps}>
      <SandpackBridge bridgeRef={bridgeRef} primaryFile={primaryFile} />
      <SandpackLayout
        style={{
          borderRadius: 0,
          border: "none",
          height: "100%",
          minHeight: 0,
        }}
      >
        {showExplorer && showCode && (
          <SandpackFileExplorer style={{ height: "100%", minWidth: 180 }} />
        )}
        {showCode && (
          <SandpackCodeEditor
            showLineNumbers
            showInlineErrors
            wrapContent
            style={{ height: "100%", flex: codeFlex, minWidth: 0 }}
          />
        )}
        {showPreview && (
          <div
            className="sandpack-web-preview"
            style={{
              display: "flex",
              flexDirection: "column",
              flex: previewFlex,
              minWidth: 0,
              minHeight: 0,
              height: "100%",
              background: "#05060a",
            }}
          >
            <SandpackPreview
              style={{
                flex: 1,
                minHeight: 0,
                border: "none",
                background: "white",
              }}
              showOpenInCodeSandbox={false}
              showRefreshButton
            />
            {showConsole && (
              <SandpackConsole
                style={{
                  height: 120,
                  borderTop: "1px solid rgba(255,255,255,0.06)",
                }}
                resetOnPreviewRestart
              />
            )}
          </div>
        )}
      </SandpackLayout>
    </SandpackProvider>
  );
});

export default ProjectSandpack;
